/**
 * Mesure du copilote — les quatre chiffres du plan « Zéro temps mort ».
 *
 * Quatre chiffres, pas davantage : les deux premiers disent si l'outil est
 * utilisable (délai des suggestions, blancs que le client entend), les deux
 * derniers s'il sert à quelque chose (suggestions utilisées, rendez-vous
 * obtenus avec et sans copilote). Chaque tuile rappelle sa cible ; le tableau
 * décline les mêmes chiffres par commercial — c'est le support de la revue
 * hebdomadaire : quinze minutes pour décider ce qu'on change la semaine
 * suivante.
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw } from "lucide-react";
import api from "@/lib/crmApi";

const WINDOWS = [
  { days: 7, label: "7 jours" },
  { days: 30, label: "30 jours" },
];

const fmtMs = (ms) => (ms == null ? "—" : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const fmtPct = (share) => (share == null ? "—" : `${Math.round(share * 100)} %`);
const fmtRate = (meetings, calls) => (calls ? `${meetings}/${calls} (${Math.round((meetings / calls) * 100)} %)` : "—");

function Tile({ label, value, detail, target }) {
  return (
    <div className="rounded-2xl border border-[#e6e8ee] bg-white p-4">
      <div className="text-[12.5px] font-medium text-[#61667a]">{label}</div>
      <div className="mt-1 font-heading text-[26px] font-bold tabular-nums tracking-[-.01em] text-[#16181d]">
        {value}
      </div>
      {detail && <div className="mt-0.5 text-[12px] text-[#71768a]">{detail}</div>}
      {target && (
        <div className="mt-1.5 font-mono text-[10.5px] uppercase tracking-[.08em] text-[#9aa0b2]">
          cible : {target}
        </div>
      )}
    </div>
  );
}

export default function CopilotKpis() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  const load = useCallback(async (window) => {
    setData(null); setError(false);
    try {
      const { data: payload } = await api.get(`/copilot/kpis?days=${window}`);
      setData(payload);
    } catch {
      setError(true);
    }
  }, []);

  useEffect(() => { load(days); }, [load, days]);

  if (error) {
    return (
      <p className="rounded-2xl border border-[#e6e8ee] bg-white px-5 py-8 text-center text-[13px] text-[#8a90a3]">
        Indicateurs indisponibles pour le moment.
      </p>
    );
  }
  if (data === null) {
    return (
      <p className="flex items-center justify-center gap-2 py-12 text-sm text-[#8a90a3]">
        <RefreshCw size={14} className="animate-spin" /> Calcul des indicateurs…
      </p>
    );
  }

  const t = data.totals || {};
  const commercials = (data.per_commercial || []).filter(
    (c) => c.calls || c.calls_without || c.suggestions);

  return (
    <div data-testid="copilot-kpis">
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[70ch] text-[13.5px] text-[#61667a] text-pretty">
          Les deux premiers chiffres disent si le copilote est utilisable, les deux derniers
          s'il fait gagner des rendez-vous. Par commercial ci-dessous, pour la revue hebdomadaire.
        </p>
        <div className="flex gap-1.5">
          {WINDOWS.map((w) => (
            <button key={w.days} onClick={() => setDays(w.days)}
              className={`rounded-[10px] border px-3 py-1.5 text-[12.5px] font-medium ${days === w.days
                ? "border-[#b9b1ff] bg-[#eeecff] text-[#3a2cc8]"
                : "border-[#e2e5ec] bg-white text-[#4c5164] hover:border-[#c9cedb]"}`}>
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 min-[1181px]:grid-cols-4">
        <Tile label="Délai des suggestions" value={fmtMs(t.latency_avg_ms)}
          detail={t.prepared_share != null ? `${fmtPct(t.prepared_share)} servies depuis le préparé` : null}
          target="< 1 s ressenti" />
        <Tile label="Blancs > 3 s par appel"
          value={t.long_silences_per_call == null ? "—" : t.long_silences_per_call}
          detail={t.calls ? `${t.calls} appel${t.calls > 1 ? "s" : ""} copiloté${t.calls > 1 ? "s" : ""}` : "aucun appel copiloté"}
          target="0 par appel" />
        <Tile label="Suggestions utilisées" value={fmtPct(t.usage_rate)}
          detail={t.suggestions ? `${t.used} sur ${t.suggestions} affichées` : "aucune suggestion affichée"}
          target="> 50 %" />
        <Tile label="Rendez-vous obtenus"
          value={fmtRate(t.meetings_with, t.calls_with)}
          detail={`sans copilote : ${fmtRate(t.meetings_without, t.calls_without)}`}
          target="avec > sans" />
      </div>

      <h3 className="mb-2 mt-6 font-mono text-[11px] uppercase tracking-[.12em] text-[#8a90a3]">
        Par commercial
      </h3>
      {commercials.length === 0 ? (
        <p className="rounded-2xl border border-[#e6e8ee] bg-white px-5 py-8 text-center text-[13px] text-[#8a90a3]">
          Aucun appel sur la fenêtre — les chiffres apparaîtront dès les premiers appels.
        </p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-[#e6e8ee] bg-white">
          <table className="w-full min-w-[720px] text-[13px]">
            <thead>
              <tr className="border-b border-[#eef0f4] text-left font-mono text-[10.5px] uppercase tracking-[.08em] text-[#8a90a3]">
                <th className="px-4 py-2.5 font-medium">Commercial</th>
                <th className="px-4 py-2.5 font-medium">Délai</th>
                <th className="px-4 py-2.5 font-medium">Préparées</th>
                <th className="px-4 py-2.5 font-medium">Blancs / appel</th>
                <th className="px-4 py-2.5 font-medium">Utilisées</th>
                <th className="px-4 py-2.5 font-medium">RDV copilotés</th>
                <th className="px-4 py-2.5 font-medium">RDV sans</th>
              </tr>
            </thead>
            <tbody>
              {commercials.map((c) => (
                <tr key={c.id} className="border-b border-[#f4f5f8] last:border-0">
                  <td className="px-4 py-2.5 font-medium text-[#16181d]">{c.name || c.id}</td>
                  <td className="px-4 py-2.5 tabular-nums text-[#4c5164]">{fmtMs(c.latency_avg_ms)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-[#4c5164]">{fmtPct(c.prepared_share)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-[#4c5164]">
                    {c.long_silences_per_call == null ? "—" : c.long_silences_per_call}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-[#4c5164]">
                    {fmtPct(c.usage_rate)}{c.suggestions ? ` (${c.used}/${c.suggestions})` : ""}
                  </td>
                  <td className="px-4 py-2.5 tabular-nums text-[#4c5164]">{fmtRate(c.meetings_with, c.calls_with)}</td>
                  <td className="px-4 py-2.5 tabular-nums text-[#4c5164]">{fmtRate(c.meetings_without, c.calls_without)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
